import React, { useState } from 'react';
import { AgentAvatar, PALETTES, parseSeed } from './AgentAvatar';
import { useWallet } from '../hooks/useWallet';
import { useModalAlert } from './ModalAlert';

const formatTimeAgo = (isoString) => {
  if (!isoString) return 'Never';
  const s = String(isoString);
  const date = new Date(s.endsWith('Z') || s.includes('+') ? s : s + 'Z');
  const diffMs = Date.now() - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

export const AgentSummaryCard = ({ agent, config, portfolioCount = 0, onClick, onDelete = null }) => {
  const { avatarSeed, paletteIndex } = parseSeed(agent.avatar_seed);
  const { jwtToken } = useWallet();
  const { showWarning, showError, showSuccess } = useModalAlert();
  const [isDeleting, setIsDeleting] = useState(false);
  const canDelete = portfolioCount === 0;

  const handleDelete = async (e) => {
    e.stopPropagation(); // Prevent card click

    if (!canDelete) {
      showError(`Cannot delete agent with active portfolios. Remove this agent from all ${portfolioCount} portfolio/portfolios first.`);
      return;
    }

    showWarning(
      'Delete Agent',
      `Are you sure you want to delete agent "${agent.name}"?\n\nThis action cannot be undone and will permanently remove this agent.`,
      {
        showCancel: true,
        confirmText: 'Delete Agent',
        cancelText: 'Cancel',
        onConfirm: async () => {
          setIsDeleting(true);
          try {
            const apiBaseUrl = config?.apiBaseUrl || 'http://localhost:8000';
            const res = await fetch(`${apiBaseUrl}/agents/${agent.id}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${jwtToken}`,
                'Content-Type': 'application/json',
              },
            });

            if (!res.ok) {
              throw new Error(`Delete failed (${res.status})`);
            }

            showSuccess(`Agent "${agent.name}" has been deleted.`);
            if (onDelete) {
              onDelete(agent.id);
            }
          } catch (err) {
            console.error('Failed to delete agent:', err);
            showError('Failed to delete agent: ' + err.message);
          } finally {
            setIsDeleting(false);
          }
        }
      }
    );
  };

  return (
    <div
      onClick={onClick}
      className="cursor-pointer bg-gray-800 border border-gray-700 hover:border-cyan-600 rounded-lg p-4 transition-all hover:shadow-lg hover:shadow-cyan-900/20"
    >
      <div className="flex items-center gap-3 mb-2">
        <AgentAvatar
          name={agent.name}
          seed={avatarSeed}
          colors={PALETTES[paletteIndex] || PALETTES[0]}
          size={36}
        />
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-white text-sm truncate">{agent.name}</h3>
          <span className="text-xs bg-gray-700 text-gray-300 rounded-full px-2 py-0.5">
            {portfolioCount} portfolio{portfolioCount !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="relative group">
          <button
            onClick={handleDelete}
            disabled={isDeleting || !canDelete}
            className="p-1.5 rounded-md bg-red-900/40 text-red-400 hover:bg-red-900/60 hover:text-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            title={canDelete ? "Delete agent" : "Cannot delete agents with portfolios"}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          {!canDelete && (
            <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block bg-slate-900 text-red-300 text-xs px-2 py-1 rounded whitespace-nowrap border border-red-700/50">
              Cannot delete agents with portfolios
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Last active: {formatTimeAgo(agent.last_interaction_at)}
      </p>
    </div>
  );
};
